/**
 * Kana ↔ Hepburn romaji, and hiragana ↔ katakana.
 *
 * Sources:
 *
 * - The Library of Congress "Japanese Romanization Table", 2022 version
 *   (loc.gov/catdir/cpso/romanization/japanese.pdf): the standard and extended kana charts
 *   (Tables A and B), the long-vowel rules of §2.5 (macron for ā ū ē ō, long i written ii except
 *   after a lengthening bar, ō for おう and おお), the apostrophe of §5.2 (n' before a vowel or y),
 *   the sokuon note (double the consonant, tch for ち), the particles は → wa and へ → e, を →
 *   o, ゐ → i, ゑ → e, and ん always n (never m). That is the "modified" or revised Hepburn
 *   romanization of ANSI Z39.11-1972, which the table cites.
 * - Revised Hepburn as described in the Wikipedia article "Hepburn romanization" and in the
 *   Hepburn tradition generally, for the options: macrons omitted (Tokyo) and long vowels
 *   written as spelled in kana (Toukyou, the "wāpuro" style).
 *
 * What a program cannot do: kanji are not read (a kanji is left unchanged and counted),
 * adjacent vowels across a morpheme boundary are written as separate vowels in the LC table
 * (koushi, not kōshi) but a program cannot see morpheme boundaries, and a particle は is
 * told from a は inside a word only when it stands alone with spaces round it. Going back,
 * ō could be おう or おお and wa, e and o could be particles, so the tool picks the
 * commoner reading and says so.
 */

export type LongVowels = "macron" | "plain" | "kana";
export type KanaScript = "hiragana" | "katakana";
export type Spelling = "hepburn" | "kunrei";
export type LongO = "ou" | "oo";

export interface RomajiOptions {
  /** Kana → romaji: how long vowels are written. */
  longVowels: LongVowels;
  /** Kana → romaji: read a standalone は as wa and へ as e. */
  particles: boolean;
  /** Kana → romaji: capitalize the first letter of each space-separated word. */
  capitalize: boolean;
  /** Romaji → kana: output script. */
  script: KanaScript;
  /** Romaji → kana: how ti, tu, si, zi, di, du, tyu are read. */
  spelling: Spelling;
  /** Romaji → kana: ō as おう or おお. */
  longO: LongO;
}

export interface RomajiResult {
  text: string;
  /** Characters left unchanged because they could not be converted. */
  unknown: string[];
  /** Kanji left unchanged (kana → romaji only). */
  kanji: number;
}

/**
 * Hiragana, then romaji. The first romaji is what the kana is written as; the others are also
 * read when going back. A leading "!" means kana → romaji only.
 */
const SPEC = `
あ:a い:i う:u え:e お:o
か:ka き:ki く:ku け:ke こ:ko
さ:sa し:shi/si す:su せ:se そ:so
た:ta ち:chi/ti つ:tsu/tu て:te と:to
な:na に:ni ぬ:nu ね:ne の:no
は:ha ひ:hi ふ:fu/hu へ:he ほ:ho
ま:ma み:mi む:mu め:me も:mo
や:ya ゆ:yu よ:yo
ら:ra り:ri る:ru れ:re ろ:ro
わ:wa ゐ:!i ゑ:!e を:!o/wo
が:ga ぎ:gi ぐ:gu げ:ge ご:go
ざ:za じ:ji/zi ず:zu ぜ:ze ぞ:zo
だ:da ぢ:!ji/di づ:!zu/du で:de ど:do
ば:ba び:bi ぶ:bu べ:be ぼ:bo
ぱ:pa ぴ:pi ぷ:pu ぺ:pe ぽ:po
ゔ:vu
きゃ:kya きゅ:kyu きょ:kyo きぇ:kye
ぎゃ:gya ぎゅ:gyu ぎょ:gyo ぎぇ:gye
しゃ:sha/sya しゅ:shu/syu しょ:sho/syo しぇ:she
じゃ:ja/zya じゅ:ju/zyu じょ:jo/zyo じぇ:je
ちゃ:cha/tya ちゅ:chu/tyu ちょ:cho/tyo ちぇ:che
ぢゃ:!ja/dya ぢゅ:!ju ぢょ:!jo/dyo ぢぇ:!je
にゃ:nya にゅ:nyu にょ:nyo にぇ:nye
ひゃ:hya ひゅ:hyu ひょ:hyo ひぇ:hye
びゃ:bya びゅ:byu びょ:byo びぇ:bye
ぴゃ:pya ぴゅ:pyu ぴょ:pyo ぴぇ:pye
みゃ:mya みゅ:myu みょ:myo みぇ:mye
りゃ:rya りゅ:ryu りょ:ryo りぇ:rye
いぇ:ye うぃ:wi うぇ:we うぉ:!wo うぁ:!wa
くぁ:kwa くぃ:kwi くぇ:kwe くぉ:kwo
ぐぁ:gwa ぐぃ:gwi ぐぇ:gwe ぐぉ:gwo
すぃ:si すぇ:swe ずぃ:zi
つぁ:tsa つぃ:tsi つぇ:tse つぉ:tso
てぃ:ti てゅ:tyu とぅ:tu
でぃ:di でゅ:dyu どぅ:du
ふぁ:fa ふぃ:fi ふぇ:fe ふぉ:fo ふゅ:fyu ふょ:fyo
ゔぁ:va ゔぃ:vi ゔぇ:ve ゔぉ:vo ゔゅ:vyu
ぁ:!a ぃ:!i ぅ:!u ぇ:!e ぉ:!o ゃ:!ya ゅ:!yu ょ:!yo ゎ:!wa ゕ:!ka ゖ:!ke
`;

/** Syllables that Kunrei-shiki and Nihon-shiki spell differently from the Hepburn table. */
const KUNREI: Record<string, string> = {
  si: "し",
  ti: "ち",
  tu: "つ",
  zi: "じ",
  di: "ぢ",
  du: "づ",
  tyu: "ちゅ",
  dyu: "ぢゅ",
};

const WHITESPACE = /\s+/;
const MARK_SPACES = /([,.?!]) {2,}/g;
const TRAILING_SPACES = / +(?=\n|$)/g;
const HIRA_START = 0x30_41;
const HIRA_END = 0x30_96;
const KATA_OFFSET = 0x60;
const HALFWIDTH = /[ｦ-ﾟ]+/g;
const KANJI = /[㐀-䶿一-鿿豈-﫿々〆〇]/;
const KANA_OR_BAR = /[ぁ-ゖァ-ヺーゝゞヽヾ]/;
const MACRON = "̄";
const SMALL_TSU = "っ";
const N_KANA = "ん";
const BAR = "ー";
const PURE_VOWELS = new Set([..."あいうえお"]);
const VOWELS_ROMAJI = "aiueo";
const MACRON_VOWELS: Record<string, string> = {
  a: "ā",
  i: "ī",
  u: "ū",
  e: "ē",
  o: "ō",
};
const HIRA_VOWEL: Record<string, string> = {
  a: "あ",
  i: "い",
  u: "う",
  e: "え",
  o: "お",
};
const PUNCTUATION: Record<string, string> = {
  "、": ", ",
  "。": ". ",
  "？": "? ",
  "！": "! ",
  "「": '"',
  "」": '"',
  "『": '"',
  "』": '"',
  "～": "-",
  "＝": "=",
  "〈": "<",
  "〉": ">",
  "《": "<",
  "》": ">",
  "〔": "[",
  "〕": "]",
  "（": "(",
  "）": ")",
  "・": " ",
  "　": " ",
};
const VOICE_PAIRS: Record<string, string> = {
  k: "g",
  s: "z",
  t: "d",
  h: "b",
  sh: "j",
  ch: "j",
  ts: "z",
  f: "b",
};
const ROMAJI_START = new Set([..."bcdfghjklmnpqrstvwxyz"]);
const DOUBLING = new Set([..."bcdfghjkpqrstvwxyz"]);

interface SyllableTables {
  forward: Map<string, string>;
  reverse: Map<string, string>;
}

function buildTables(): SyllableTables {
  const forward = new Map<string, string>();
  const reverse = new Map<string, string>();
  for (const entry of SPEC.trim().split(WHITESPACE)) {
    const [kana, rest] = entry.split(":") as [string, string];
    const forwardOnly = rest.startsWith("!");
    const readings = rest.replace("!", "").split("/");
    forward.set(kana, readings[0] as string);
    if (!forwardOnly) {
      reverse.set(readings[0] as string, kana);
    }
    for (const alt of readings.slice(1)) {
      reverse.set(alt, kana);
    }
  }
  return { forward, reverse };
}

const TABLES = buildTables();
const HEPBURN_REVERSE = TABLES.reverse;
const KUNREI_REVERSE = new Map(TABLES.reverse);
for (const [romaji, kana] of Object.entries(KUNREI)) {
  KUNREI_REVERSE.set(romaji, kana);
}

// ---------------------------------------------------------------------------------------
// Hiragana ↔ katakana
// ---------------------------------------------------------------------------------------

function toKatakanaChar(ch: string): string {
  const code = ch.codePointAt(0) ?? 0;
  if (code >= HIRA_START && code <= HIRA_END) {
    return String.fromCodePoint(code + KATA_OFFSET);
  }
  if (code === 0x30_9d || code === 0x30_9e) {
    return String.fromCodePoint(code + KATA_OFFSET);
  }
  return ch;
}

function toHiraganaChar(ch: string): string {
  const code = ch.codePointAt(0) ?? 0;
  const lower = code - KATA_OFFSET;
  if (
    (lower >= HIRA_START && lower <= HIRA_END) ||
    code === 0x30_fd ||
    code === 0x30_fe
  ) {
    return String.fromCodePoint(lower);
  }
  return ch;
}

function foldHalfwidth(text: string): string {
  return text.replace(HALFWIDTH, (run) => run.normalize("NFKC"));
}

/** Hiragana → katakana (half-width katakana is widened first). */
export function toKatakana(text: string): string {
  return [...foldHalfwidth(text).normalize("NFC")].map(toKatakanaChar).join("");
}

/** Katakana → hiragana. The lengthening bar ー and katakana-only letters stay as they are. */
export function toHiragana(text: string): string {
  return [...foldHalfwidth(text).normalize("NFC")].map(toHiraganaChar).join("");
}

// ---------------------------------------------------------------------------------------
// Kana → romaji
// ---------------------------------------------------------------------------------------

type Item =
  | { t: "syl"; rom: string; pure: boolean }
  | { t: "sok" }
  | { t: "n" }
  | { t: "bar" }
  | { t: "raw"; text: string };

interface ForwardState {
  items: Item[];
  unknown: Set<string>;
  kanji: number;
}

function isStandalone(chars: readonly string[], i: number): boolean {
  const before = chars[i - 1];
  const after = chars[i + 1];
  const isWordChar = (c: string | undefined) =>
    c !== undefined && (KANA_OR_BAR.test(c) || KANJI.test(c));
  return !(isWordChar(before) || isWordChar(after));
}

function pushRaw(state: ForwardState, ch: string): void {
  const mapped = PUNCTUATION[ch];
  if (mapped !== undefined) {
    state.items.push({ t: "raw", text: mapped });
    return;
  }
  if (KANJI.test(ch)) {
    state.kanji += 1;
    state.unknown.add(ch);
  }
  state.items.push({ t: "raw", text: ch });
}

function repeatMark(prev: Item | undefined, voiced: boolean): Item | undefined {
  if (prev?.t !== "syl") {
    return;
  }
  const rom = prev.rom;
  if (!voiced) {
    return { t: "syl", rom, pure: false };
  }
  const head = ["sh", "ch", "ts"].find((digraph) => rom.startsWith(digraph));
  const key = head ?? rom.slice(0, 1);
  const voicedHead = VOICE_PAIRS[key];
  if (!voicedHead) {
    return { t: "syl", rom, pure: false };
  }
  return {
    t: "syl",
    rom: voicedHead + rom.slice(key.length),
    pure: false,
  };
}

function pushKana(
  state: ForwardState,
  hira: readonly string[],
  i: number,
  particles: boolean
): number {
  const ch = hira[i] as string;
  const pair = ch + (hira[i + 1] ?? "");
  const double = TABLES.forward.get(pair);
  if (double && pair.length === 2) {
    state.items.push({ t: "syl", rom: double, pure: false });
    return 2;
  }
  if (particles && (ch === "は" || ch === "へ") && isStandalone(hira, i)) {
    state.items.push({ t: "syl", rom: ch === "は" ? "wa" : "e", pure: false });
    return 1;
  }
  const single = TABLES.forward.get(ch);
  if (single) {
    state.items.push({ t: "syl", rom: single, pure: PURE_VOWELS.has(ch) });
    return 1;
  }
  return 0;
}

function tokenizeKana(text: string, particles: boolean): ForwardState {
  const normalized = text.normalize("NFKC");
  const hira = [...normalized].map(toHiraganaChar);
  const original = [...normalized];
  const state: ForwardState = {
    items: [],
    unknown: new Set<string>(),
    kanji: 0,
  };
  let i = 0;
  while (i < hira.length) {
    const ch = hira[i] as string;
    if (ch === SMALL_TSU) {
      state.items.push({ t: "sok" });
      i += 1;
    } else if (ch === N_KANA) {
      state.items.push({ t: "n" });
      i += 1;
    } else if (ch === BAR) {
      state.items.push({ t: "bar" });
      i += 1;
    } else if (ch === "ゝ" || ch === "ゞ") {
      const mark = repeatMark(state.items.at(-1), ch === "ゞ");
      state.items.push(mark ?? { t: "raw", text: original[i] as string });
      i += 1;
    } else {
      const used = pushKana(state, hira, i, particles);
      if (used > 0) {
        i += used;
      } else {
        pushRaw(state, original[i] as string);
        i += 1;
      }
    }
  }
  return state;
}

function vowelOf(rom: string): string {
  const last = rom.at(-1) ?? "";
  return VOWELS_ROMAJI.includes(last) ? last : "";
}

/** Merge a following vowel or lengthening bar into the syllable; returns the new text and items used. */
function lengthen(
  text: string,
  next: Item | undefined,
  mode: LongVowels
): { text: string; used: number } {
  const v = vowelOf(text);
  if (!v) {
    return { text, used: 0 };
  }
  if (next?.t === "bar") {
    if (mode === "kana") {
      return { text: text + v, used: 1 };
    }
    return { text: mode === "plain" ? text : swapMacron(text, v), used: 1 };
  }
  if (mode === "kana" || next?.t !== "syl" || !next.pure) {
    return { text, used: 0 };
  }
  const pair = v + next.rom;
  const long = pair === "aa" || pair === "uu" || pair === "ee" || pair === "oo";
  if (long || pair === "ou") {
    return {
      text: mode === "plain" ? text : swapMacron(text, v),
      used: 1,
    };
  }
  return { text, used: 0 };
}

function swapMacron(text: string, vowel: string): string {
  return text.slice(0, -1) + (MACRON_VOWELS[vowel] ?? vowel);
}

function doubleConsonant(next: Item | undefined): string {
  if (next?.t !== "syl" || VOWELS_ROMAJI.includes(next.rom[0] ?? "a")) {
    return "t";
  }
  return next.rom.startsWith("ch") ? "t" : (next.rom[0] as string);
}

function nText(next: Item | undefined): string {
  if (next?.t === "syl" && "aiueoy".includes(next.rom[0] ?? "")) {
    return "n'";
  }
  return "n";
}

function renderItems(items: readonly Item[], mode: LongVowels): string {
  let out = "";
  let i = 0;
  while (i < items.length) {
    const item = items[i] as Item;
    const next = items[i + 1];
    i += 1;
    if (item.t === "raw") {
      out += item.text;
    } else if (item.t === "sok") {
      out += doubleConsonant(next);
    } else if (item.t === "n") {
      out += nText(next);
    } else if (item.t === "bar") {
      out += mode === "kana" ? "-" : "";
    } else {
      const merged = lengthen(item.rom, next, mode);
      out += merged.text;
      i += merged.used;
    }
  }
  return out;
}

function capitalizeWords(text: string): string {
  return text
    .split(" ")
    .map((word) => (word ? word[0]?.toUpperCase() + word.slice(1) : word))
    .join(" ");
}

/** Romanize kana (hiragana, katakana and half-width katakana) in Hepburn; kanji and other characters pass through. */
export function kanaToRomaji(
  input: string,
  options: RomajiOptions
): RomajiResult {
  const state = tokenizeKana(input, options.particles);
  const text = renderItems(state.items, options.longVowels)
    .replace(MARK_SPACES, "$1 ")
    .replace(TRAILING_SPACES, "");
  return {
    text: (options.capitalize ? capitalizeWords(text) : text).normalize("NFC"),
    unknown: [...state.unknown],
    kanji: state.kanji,
  };
}

// ---------------------------------------------------------------------------------------
// Romaji → kana
// ---------------------------------------------------------------------------------------

const MAX_ROMAJI = 3;
const LETTER = /\p{L}/u;

interface ReverseState {
  out: string;
  lastVowel: string;
  unknown: Set<string>;
}

function matchRomaji(
  chars: readonly string[],
  at: number,
  table: ReadonlyMap<string, string>
): { kana: string; length: number } | undefined {
  for (let length = MAX_ROMAJI; length > 0; length -= 1) {
    const key = chars.slice(at, at + length).join("");
    const kana = key.length === length ? table.get(key) : undefined;
    if (kana) {
      return { kana, length };
    }
  }
  return;
}

function lengthMark(state: ReverseState, options: RomajiOptions): string {
  if (options.script === "katakana") {
    return BAR;
  }
  if (state.lastVowel === "o" && options.longO === "ou") {
    return "う";
  }
  return HIRA_VOWEL[state.lastVowel] ?? "";
}

function handleConsonant(
  chars: readonly string[],
  i: number,
  table: ReadonlyMap<string, string>
): { kana: string; used: number } | undefined {
  const ch = chars[i] as string;
  const next = chars[i + 1];
  if (ch === "n" && (next === "'" || next === "’")) {
    return { kana: N_KANA, used: 2 };
  }
  if (ch === "t" && next === "c" && chars[i + 2] === "h") {
    return { kana: SMALL_TSU, used: 1 };
  }
  if (DOUBLING.has(ch) && next === ch) {
    return { kana: SMALL_TSU, used: 1 };
  }
  if (matchRomaji(chars, i, table)) {
    return;
  }
  if (ch === "n") {
    return { kana: N_KANA, used: 1 };
  }
  if (ch === "m" && next !== undefined && "bmp".includes(next)) {
    return { kana: N_KANA, used: 1 };
  }
  return;
}

function reverseStep(
  state: ReverseState,
  chars: readonly string[],
  i: number,
  table: ReadonlyMap<string, string>,
  options: RomajiOptions
): number {
  const ch = chars[i] as string;
  if (ch === MACRON && state.lastVowel) {
    state.out += lengthMark(state, options);
    state.lastVowel = "";
    return 1;
  }
  const special = ROMAJI_START.has(ch)
    ? handleConsonant(chars, i, table)
    : undefined;
  if (special) {
    state.out += special.kana;
    state.lastVowel = "";
    return special.used;
  }
  const hit = matchRomaji(chars, i, table);
  if (hit) {
    state.out += hit.kana;
    state.lastVowel = vowelOfKana(hit.kana);
    return hit.length;
  }
  state.lastVowel = "";
  if (LETTER.test(ch)) {
    state.unknown.add(ch.normalize("NFC"));
  }
  state.out += ch;
  return 1;
}

const KANA_VOWEL: Record<string, string> = {
  あ: "a",
  ぁ: "a",
  い: "i",
  ぃ: "i",
  う: "u",
  ぅ: "u",
  え: "e",
  ぇ: "e",
  お: "o",
  ぉ: "o",
  ゃ: "a",
  ゅ: "u",
  ょ: "o",
};

const A_ROW = new Set([..."かさたなはまやらわがざだばぱ"]);
const I_ROW = new Set([..."きしちにひみりぎじぢびぴゐ"]);
const U_ROW = new Set([..."くすつぬふむゆるぐずづぶぷゔ"]);
const E_ROW = new Set([..."けせてねへめれげぜでべぺゑ"]);
const O_ROW = new Set([..."こそとのほもよろをごぞどぼぽ"]);

/** The vowel a kana ends in, so a following macron knows what to lengthen. */
function vowelOfKana(kana: string): string {
  const last = kana.at(-1) ?? "";
  const small = KANA_VOWEL[last];
  if (small) {
    return small;
  }
  if (A_ROW.has(last)) {
    return "a";
  }
  if (I_ROW.has(last)) {
    return "i";
  }
  if (U_ROW.has(last)) {
    return "u";
  }
  if (E_ROW.has(last)) {
    return "e";
  }
  return O_ROW.has(last) ? "o" : "";
}

/** Convert Hepburn (or Kunrei-shiki) romaji to kana. */
export function romajiToKana(
  input: string,
  options: RomajiOptions
): RomajiResult {
  const table =
    options.spelling === "kunrei" ? KUNREI_REVERSE : HEPBURN_REVERSE;
  const chars = [...input.normalize("NFD").toLowerCase()];
  const state: ReverseState = {
    out: "",
    lastVowel: "",
    unknown: new Set<string>(),
  };
  let i = 0;
  while (i < chars.length) {
    i += reverseStep(state, chars, i, table, options);
  }
  const text =
    options.script === "katakana" ? toKatakana(state.out) : state.out;
  return { text: text.normalize("NFC"), unknown: [...state.unknown], kanji: 0 };
}

// ---------------------------------------------------------------------------------------
// Chart
// ---------------------------------------------------------------------------------------

export interface KanaChartRow {
  group: "basic" | "voiced" | "yoon" | "extended";
  hiragana: string;
  katakana: string;
  romaji: string;
}

const VOICED_START = "が";
const YOON_START = "きゃ";
const EXTENDED_START = "いぇ";
const SMALL_KANA = "ぁぃぅぇぉゃゅょゎゕゖ";

function chartRow(
  group: KanaChartRow["group"],
  hiragana: string,
  romaji: string
): KanaChartRow {
  return { group, hiragana, katakana: toKatakana(hiragana), romaji };
}

/** The kana chart: every syllable with its Hepburn value. */
export function kanaChartRows(): KanaChartRow[] {
  const rows: KanaChartRow[] = [];
  let group: KanaChartRow["group"] = "basic";
  for (const [kana, romaji] of TABLES.forward) {
    if (kana === VOICED_START) {
      rows.push(chartRow("basic", N_KANA, "n"));
      group = "voiced";
    } else if (kana === YOON_START) {
      group = "yoon";
    } else if (kana === EXTENDED_START) {
      group = "extended";
    }
    if (!(kana.length === 1 && SMALL_KANA.includes(kana))) {
      rows.push(chartRow(group, kana, romaji));
    }
  }
  return rows;
}
